import { FC, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { CollapsableCard } from '@/_shared/components/CollapsableCard';
import { StyledTableDrawer, usePaginatedTable } from '@/_shared/components/Table';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { GenericApiError } from '@/_shared/types/apiError';
import { PAGE_SIZE_TEAM_MEMBERS } from '@/departments/state/constants';
import { getDepartmentTeamDetailsCall, getDepartmentTeamMembersCall } from '../../state';
import { TableArea } from './TableArea';

type Props = {
  departmentId?: string;
  teamId?: string;
  teamName?: string;
  loading?: boolean;
  error?: GenericApiError | undefined;
  success?: boolean;
};

export const TabDepartmentTeam: FC<Props> = ({
  departmentId,
  teamId,
  teamName,
  loading,
  error,
  success,
}) => {
  const t = useTranslations('departments.details');
  const { genericResponsiveValue } = useResponsiveStyle();

  const tShared = useTranslations();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    execute: executeGetDepartmentTeamMembers,
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    reset: resetGetDepartmentTeamMembers,
    data,
    totalItems,
  } = getDepartmentTeamMembersCall.hook();
  const {
    execute: executeGetDepartmentTeamDetails,
    loading: loadingGetDepartmentTeamDetails,
    reset: resetGetDepartmentTeamDetails,
    error: errorGetDepartmentTeamDetails,
    success: successGetDepartmentTeamDetails,
    departmentTeamDetails,
  } = getDepartmentTeamDetailsCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_TEAM_MEMBERS,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  useEffect(() => {
    if (teamId && departmentId) {
      executeGetDepartmentTeamMembers({ teamId, departmentId, excludeMemberType: ['teamManager'] });
      executeGetDepartmentTeamDetails({ teamId, departmentId });
    }
    return () => {
      resetGetDepartmentTeamMembers();
      resetGetDepartmentTeamDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, teamId]);

  if (loading || loadingGetDepartmentTeamDetails || loadingGetDepartmentTeamMembers) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (error || errorGetDepartmentTeamMembers || errorGetDepartmentTeamDetails) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (success && !loading && !error) {
    return (
      <Box>
        <Card mt='m' mb='s' display='flex' flexDirection='column'>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
          >
            <Typography variant='h1'>
              {teamName || departmentTeamDetails?.name !== 'unknown'
                ? departmentTeamDetails?.name
                : t('noTeamName')}
            </Typography>
            <Box
              ml={genericResponsiveValue({
                breakPoints: { desktop: 'xl' },
                defaultValue: 'none',
              })}
              mt={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xxs',
              })}
              alignItems='center'
              flexDirection='row'
              display='flex'
            >
              <Svg name='users' size='s' />
              <Typography ml='xxs' variant='h1'>
                {departmentTeamDetails?.stats?.totalEmployees || 0}
              </Typography>
            </Box>
          </Box>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems='flex-start'
            width={genericResponsiveValue({
              breakPoints: { desktop: '50%' },
              defaultValue: '100%',
            })}
            mb='s'
            backgroundColor={genericResponsiveValue({
              breakPoints: { desktop: 'transparent' },
              defaultValue: 'grey/dark',
            })}
            p={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 's',
            })}
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'xs',
            })}
            borderRadius={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'm',
            })}
          >
            <Box flex={1} flexDirection='column' display='flex'>
              <Typography
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'xs' },
                  defaultValue: 'none',
                })}
                variant='p2/light'
                color='grey/light'
              >
                {t('validProfiles')}
              </Typography>
              <Typography variant='h1'>
                {departmentTeamDetails?.stats?.completedEmployees}
              </Typography>
            </Box>
            <Box flex={1} flexDirection='column' display='flex'>
              <Typography mt='xs' variant='p2/light' color='grey/light'>
                {t('profilesToCorrect')}
              </Typography>
              <Typography
                variant='h1'
                color={departmentTeamDetails?.stats?.invalidEmployees ? 'error' : 'success'}
              >
                {departmentTeamDetails?.stats?.invalidEmployees || 0}
              </Typography>
            </Box>
          </Box>
          <Box display='flex' flexDirection='column'>
            <Typography mt='xs' variant='p2/label'>
              {t('teamManager')}
            </Typography>
            {successGetDepartmentTeamDetails && (
              <TableArea
                loading={loadingGetDepartmentTeamMembers}
                error={errorGetDepartmentTeamMembers}
                paginedProps={{
                  ...paginedProps,
                  paginatedData: [departmentTeamDetails?.manager],
                }}
                drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
                hidePagination
              />
            )}
          </Box>
        </Card>
        <CollapsableCard
          title={departmentTeamDetails?.name !== 'unknown' ? t('teamMembers') : t('members')}
          isCollapsedOnDefault={false}
        >
          <Box
            width='100%'
            minWidth={0}
            minHeight={0}
            overflow={genericResponsiveValue({
              breakPoints: { desktop: 'visible' },
              defaultValue: 'auto',
            })}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <Box
              alignItems='center'
              mb='s'
              display='flex'
              flexDirection='row'
              justifyContent='flex-end'
            >
              <Box
                mt='xs'
                alignItems={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'flex-start',
                })}
                display='flex'
                flexDirection='row'
                width={genericResponsiveValue({
                  breakPoints: { desktop: 'auto' },
                  defaultValue: '100%',
                })}
              >
                <Box
                  ml={genericResponsiveValue({
                    breakPoints: { desktop: 'xl' },
                    defaultValue: 'none',
                  })}
                  alignItems='center'
                  flexDirection='row'
                  display='flex'
                  width='100%'
                >
                  <Svg name='users' size='s' />
                  <Typography ml='xxs' variant='h1'>
                    {totalItems || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <TableArea
              loading={loadingGetDepartmentTeamMembers}
              error={errorGetDepartmentTeamMembers}
              paginedProps={paginedProps}
              drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
            />
          </Box>
        </CollapsableCard>
      </Box>
    );
  }

  return null;
};
